/* eslint-disable max-len */
import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Formik } from 'formik';
import toast from 'react-hot-toast';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';

import {
    handleFetchErrors,
    onCompletedHandler,
    onErrorHandler,
} from '@/services/apollo';
import Loader from '@/components/commons/loader';
import ToggleForm from '@/components/forms/toggle_form';
import { handleColumnSort, SortDirection } from '@/services/sorting';
import WarningCard from '@/components/commons/warning_card';
import { formattedCircle } from '@/components/my-sixty/contacts-game/helpers';
// import DotsHorizontal from '@/components/commons/icons/DotsHorizontal';
import SmartTable from '@/components/commons/table/SmartTable';
import ProfileImage from '@/components/commons/ProfileImage';
import { FETCH_UNIFIED_CONTACTS } from '@/services/unifiedContacts';

import { FETCH_INTERACTIONS, UPDATE_CONTACT_VISIBILITY } from './Actions';
import {
    filteringByCircle,
    filteringByVisibility,
    findContactByID,
    formatLastInteractionDate,
    retrieveContactsId,
    selectBriefOrLinkedInIcon,
    selectCircleIcon,
    selectGroupIcon,
    selectInteractionIcon,
} from './helpers';
import ContactModal from './ContactModal';

export const DEFAULT_SORT_COLUMN = 'displayName';
export const DEFAULT_SORT_DIRECTION = SortDirection.ASC;

function ContactsTable({ filter, keyword = '' }) {
    const [contactsList, setContactsList] = useState([]);
    const [isError, setIsError] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(null);
    const [sortColumn, setSortColumn] = useState(DEFAULT_SORT_COLUMN);
    const [sortDirection, setSortDirection] = useState(DEFAULT_SORT_DIRECTION);
    const [formError, setFormError] = useState('');
    const [toggleInactive, setToggleInactive] = useState(false);
    const [lastInteractions, setLastInteractions] = useState(null);

    const [updateContactVisibility] = useMutation(UPDATE_CONTACT_VISIBILITY);

    const { refetch: refetchLastInteraction } = useQuery(FETCH_INTERACTIONS, {
        variables: {
            unifiedContactIds: retrieveContactsId(contactsList),
        },
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'no-cache',
        onError: onErrorHandler(setIsError),
        onCompleted: onCompletedHandler(setIsError, (info) => {
            const contactsData = info?.latestInteractions;
            setLastInteractions(contactsData);
        }),
    });

    const { refetch } = useQuery(FETCH_UNIFIED_CONTACTS, {
        variables: {
            sortColumn,
            sortDirection,
            visible: filteringByVisibility(filter),
            circle: filteringByCircle(filter),
            keyword,
        },
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'no-cache',
        onError: onErrorHandler(setIsError),
        onCompleted: onCompletedHandler(setIsError, (info) => {
            const contactsData = info?.user?.unifiedContacts;
            const filteredList = contactsData?.filter((contact) => contact.mappings.length > 0);
            setContactsList(filteredList);
            if (isLoading) {
                setIsLoading(false);
            }
        }),
    });

    const handleToggleVisibility = async (id, visible, _) => {
        setToggleInactive(true);
        try {
            const response = await updateContactVisibility({
                variables: {
                    updateUnifiedContactInput: {
                        id,
                        attributes: {
                            visible,
                        },
                    },
                },
            });
            if (response.data.updateUnifiedContact.errors.length === 0) {
                toast.success(`${_.displayName} was updated successfully.`);
                if (formError !== '') setFormError('');
                await refetch();
                refetchLastInteraction();
                setToggleInactive(false);
                return;
            }
            setFormError('Something went wrong.  Please try again.');
            setToggleInactive(false);
        } catch (err) {
            handleFetchErrors(err);
            setFormError('An unexpected error occurred.  Please try again later.');
            setToggleInactive(false);
        }
    };

    const onSort = handleColumnSort(sortColumn, setSortColumn, sortDirection, setSortDirection);

    const contactInfoColumn = {
        label: 'Contact',
        id: 'displayName',
        onSort: () => onSort('displayName', DEFAULT_SORT_DIRECTION),
        render: (rowId, record) => (
            <div key={rowId} className="flex items-center min-w-[280px]">
                <ProfileImage brief={record} height="min-h-[60px] max-h-[60px]" width="min-w-[60px] max-w-[60px]" externalStyles="mx-0" />
                <div className="flex flex-col items-start justify-center pl-3">
                    <button type="button" onClick={() => setIsModalOpen(record.id)} className="font-bold text-progressBarBlue text-left">{record.displayName}</button>
                    <p className="text-[14px]">{record.primaryEmail}</p>
                </div>
            </div>
        ),
    };

    const briefOrLinkedInColumn = {
        label: 'Brief/LinkedIn',
        id: 'briefOrLinkedIn',
        render: (rowId, record) => {
            if (!record.primaryBrief && !record.secondaryBrief && !record.linkedIn) {
                return <p>-</p>;
            }
            return (
                <div key={rowId} className="flex items-center min-w-[150px]">
                    <div className="mr-2">
                        {selectBriefOrLinkedInIcon(
                            record.primaryBrief || record.secondaryBrief || record.linkedIn,
                        )}
                    </div>
                    <p className={`${record.primaryBrief || record.secondaryBrief !== '' ? 'text-progressBarBlue' : ''}`}>
                        {record.primaryBrief || record.secondaryBrief || record.linkedIn}
                    </p>
                </div>
            );
        },
    };

    const lastInteractionColumn = {
        label: 'Last interaction',
        id: 'latestInteraction',
        render: (rowId, record) => {
            const latestInteraction = lastInteractions?.find((el) => el.unifiedContactId === record.id);
            if (!latestInteraction || !latestInteraction?.subject) {
                return <p>-</p>;
            }
            const subject = latestInteraction?.subject;
            const date = formatLastInteractionDate(latestInteraction.whenHappened, false);
            return (
                <div key={rowId} className="text-cleanSlate flex items-start min-w-[200px]">
                    <div className="pr-2 pt-[2px]">
                        {selectInteractionIcon(latestInteraction?.subject, false)}
                    </div>
                    <div className="flex flex-col items-start justify-start">
                        <p className="font-bold">
                            {`${subject}`}
                        </p>
                        <p>{date}</p>
                    </div>
                </div>
            );
        },
    };

    const circleColumn = {
        label: 'Circle',
        id: 'circle',
        onSort: () => onSort('circle', DEFAULT_SORT_DIRECTION),
        render: (rowId, record) => {
            if (record.effectiveCircle === null) {
                return <p>-</p>;
            }

            const formatedCircle = formattedCircle(record.effectiveCircle);
            return (
                <div key={rowId} className="text-cleanSlate flex items-center min-w-[100px]">
                    <div className="pr-[8px]">
                        {selectCircleIcon(formatedCircle)}
                    </div>
                    <p>{formatedCircle}</p>
                </div>
            );
        },
    };

    const groupsColumn = {
        label: 'Groups',
        id: 'groups',
        render: (rowId, record) => {
            if (record.mappings.length === 0) {
                return <p>-</p>;
            }

            let { kind } = record.mappings[0].contact.account;
            if (!kind) {
                return <p>-</p>;
            }
            kind = kind.charAt(0).toUpperCase() + kind.slice(1);
            const fieldName = `groups-${record.id}`;
            const initialValues = {};
            initialValues[fieldName] = record.enabled;
            return (
                <div key={rowId} className="flex items-center px-3 py-[2px] bg-shadeStoneGray rounded-lg w-min">
                    <div className="pr-1">
                        {selectGroupIcon(kind)}
                    </div>
                    <p className="font-medium">{kind}</p>
                </div>
            );
        },
    };

    const visibilityColumn = {
        label: 'Visibility',
        id: 'visibility',
        shrink: false,
        render: (rowId, record) => {
            const fieldName = `visible-${record.id}`;
            const initialValues = {};
            initialValues[fieldName] = record.visible;
            return (
                <div key={rowId} className="pr-2">
                    <Formik
                        onSubmit={() => { }}
                        initialValues={initialValues}
                    >
                        <div className="flex items-center justify-start">
                            <ToggleForm
                                record={record}
                                onChange={() => handleToggleVisibility(record.id, !record.visible, record)}
                                fieldName={fieldName}
                                disabled={toggleInactive}
                                propertyName="visible"
                            />
                        </div>
                    </Formik>
                </div>
            );
        },
    };

    // const optionsColumn = {
    //     label: '',
    //     id: 'options',
    //     shrink: false,
    //     render: (rowId) => (
    //         <div key={rowId} className="float-right pr-2">
    //             <button
    //                 type="button"
    //                 data-tooltip-id="tip"
    //                 title="options"
    //                 aria-label="options"
    //                 className="border-[1px] border-cleanSlate rounded-lg flex items-center justify-center px-3 py-2"
    //             >
    //                 <DotsHorizontal className="w-3" />
    //             </button>
    //         </div>
    //     ),
    // };

    const columnGroupDesktop = [
        contactInfoColumn,
        briefOrLinkedInColumn,
        lastInteractionColumn,
        circleColumn,
        groupsColumn,
        visibilityColumn,
        // optionsColumn,
    ];

    const columnGroupMobile = [
        contactInfoColumn,
    ];

    const externalStyles = {
        title: 'hidden',
        wrapper: 'overflow-x-auto border-b border-lightGray pb-0',
        headerCell: 'pb-0 pt-2 pl-4',
        table: 'bg-lightGray border-separate w-auto border-spacing-x-0 border-spacing-y-[16px]',
        roundedRow: true,
    };

    return (
        <Loader isLoading={isLoading || !contactsList} isError={isError}>
            {
                formError !== '' && (
                    <div className="w-full my-[20px]">
                        <WarningCard message={formError} />
                    </div>
                )
            }
            {(contactsList && contactsList.length)
                ? (
                    <>
                        <div className="hidden md:block border-shadeStoneGray bg-cirrusWhite rounded-md mt-4">
                            {(contactsList && contactsList.length)
                                && (
                                    <SmartTable
                                        columns={columnGroupDesktop}
                                        data={contactsList}
                                        sortColumn={sortColumn}
                                        sortDirection={sortDirection}
                                        paginationState={{ windowSize: 3, initialPage: 0 }}
                                        externalStyles={externalStyles}
                                    />
                                )}
                        </div>
                        <div className="block md:hidden border-shadeStoneGray bg-cirrusWhite rounded-md mt-4">
                            {(contactsList && contactsList.length)
                                && (
                                    <SmartTable
                                        columns={columnGroupMobile}
                                        data={contactsList}
                                        sortColumn={sortColumn}
                                        sortDirection={sortDirection}
                                        paginationState={{ windowSize: 3, initialPage: 0 }}
                                        externalStyles={externalStyles}
                                    />
                                )}
                        </div>
                        <Tooltip id="tip" />
                        {isModalOpen && (
                            <ContactModal
                                isModalOpen={isModalOpen}
                                setIsModalOpen={setIsModalOpen}
                                data={findContactByID(isModalOpen, contactsList, lastInteractions)}
                            />
                        )}
                    </>
                ) : (

                    !isLoading && (
                        <div className="w-full my-[20px]">
                            <WarningCard
                                message="No matching contacts found for the given search criteria."
                                bkgColor="progressBarBlue"
                            />
                        </div>
                    )

                )}
        </Loader>
    );
}

export default ContactsTable;
